import React, {useState} from 'react';
import b_ from 'b_';
import {FileError, useDropzone} from 'react-dropzone';

import imageUploader from 'client/common/utils/image-uploader';
import config from 'client/common/utils/client-config';
import ImageCropper from 'client/common/components/image-cropper';

import I18N from 'assets/i18n/brandlift-project';

import {ImageUploaderProps} from './types';

import './image-uploader.css';


const i18n = I18N.common.imageUploader;
const b = b_.with('image-uploader');

const ERROR_MESSAGES: {[key: FileError['code']]: string} = {
    'file-invalid-type': i18n.errors.wrongFileType,
    'file-too-large': i18n.errors.fileTooLarge
};

// eslint-disable-next-line max-statements
export default function ImageUploader(props: ImageUploaderProps) {
    const {
        labelText = '',
        className = '',
        disabled,
        withCropper,
        onUploadStart,
        onUploadSuccess,
        onUploadFail,
        maxSize
    } = props;

    const [image, setImage] = useState<string | null>(null);
    const [fileName, setFileName] = useState<string>('');
    const [visible, setVisible] = useState<boolean>(true);

    const handleStartUpload = async (file: File) => {
        if (!file) {
            return;
        }

        const {name, size} = file;

        if (onUploadStart) {
            onUploadStart({name, size});
        }

        const result = await imageUploader(file, maxSize);

        if (result.error && onUploadFail) {
            onUploadFail(result.error);

            return;
        }

        onUploadSuccess(result);
    };

    const handleSelectFile = (files: File[]) => {
        const [file] = files;

        if (!file) {
            return;
        }

        if (withCropper) {
            const reader = new FileReader();

            setFileName(file.name);

            reader.readAsDataURL(file);

            reader.addEventListener('load', () => {
                setImage(reader.result?.toString() || null);
                setVisible(true);
            });

            return;
        }

        void handleStartUpload(file);
    };

    const {getRootProps, getInputProps, isFocused, isDragAccept, isDragReject} = useDropzone({
        accept: ['image/jpeg', 'image/png'],
        disabled,
        maxSize: (maxSize ?? config.maxImageSize) * 1024 * 1024,
        onDropAccepted: handleSelectFile,
        onDropRejected: e => {
            const [{errors}] = e;

            const [{code}] = errors;

            const uploadFailMessage = ERROR_MESSAGES[code].replace(
                '%s',
                String(maxSize ?? config.maxImageSize)
            );

            if (onUploadFail) {
                onUploadFail(uploadFailMessage || '');
            }
        }
    });

    const renderInput = () => {
        const {children} = props;

        return children ? (
            children
        ) : (
            <div className={b('input')}>
                <div className={b('button-upload')}>{i18n.uploadImage}</div>
            </div>
        );
    };

    let classes = b('wrapper-state', {
        focused: isFocused,
        accepted: isDragAccept,
        rejected: isDragReject
    });

    if (className) {
        classes += ` ${className}`;
    }

    const handleClose = () => {
        setVisible(false);
    };

    return (
        <>
            <div {...getRootProps({className: `${classes}`})}>
                <div className={b('wrapper')}>
                    {renderInput()}
                    {labelText ? <span className={b('label')}>{labelText}</span> : null}
                    <input {...getInputProps()} />
                </div>
            </div>
            {image && (
                <ImageCropper
                    image={image}
                    name={fileName}
                    defaultAspect={16/9}
                    visible={visible}
                    handleClose={handleClose}
                    onImageCropped={handleStartUpload}
                />
            )}
        </>
    );
}
